import { gql, useQuery } from '@apollo/client';
import IconClients from '@apollo/icons/default/IconClients.svg';
import IconCopy from '@apollo/icons/default/IconCopy.svg';
import IconDoubleChevronLeft from '@apollo/icons/default/IconDoubleChevronLeft.svg';
import IconDoubleChevronRight from '@apollo/icons/default/IconDoubleChevronRight.svg';
import IconExplorer from '@apollo/icons/default/IconExplorer.svg';
import IconHistory from '@apollo/icons/default/IconHistory.svg';
import IconTime from '@apollo/icons/default/IconTime.svg';
import { ButtonGroup, Heading, IconButton, Text } from '@apollo/orbit';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import React, { useState } from 'react';

import {
  persistedQueryListRouteConfig,
  variantPersistedQueriesRouteConfig,
} from 'src/app/graph/routes';
import { ExplorerLink } from 'src/components/common/explorerLink/ExplorerLink';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { GraphqlCodeBlock } from 'src/components/graphqlCodeBlock/GraphqlCodeBlock';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const PersistedQueryDetailsDrawer = ({
  listId,
  linkedGraphVariant,
}: {
  listId: string;
  linkedGraphVariant?: string;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { graphId, selectedOperation, clientName } = useRouteParams(
    variantPersistedQueriesRouteConfig,
    persistedQueryListRouteConfig,
  );

  const {
    data: newData,
    previousData,
    loading,
  } = useQuery<
    GraphQLTypes.PersistedQueryDetailsDrawerQuery,
    GraphQLTypes.PersistedQueryDetailsDrawerQueryVariables
  >(
    gql`
      query PersistedQueryDetailsDrawerQuery(
        $graphId: ID!
        $listId: ID!
        $operationId: ID!
        $clientName: String
      ) {
        graph(id: $graphId) {
          id
          persistedQueryList(id: $listId) {
            id
            operation(id: $operationId, clientName: $clientName) {
              id
              clientName
              name
              body
              firstPublishedAt
              lastPublishedAt
            }
          }
        }
      }
    `,
    {
      variables: {
        graphId,
        listId,
        operationId: selectedOperation as string,
        clientName,
      },
      skip: !selectedOperation,
    },
  );

  const data = newData ?? previousData;
  const graph = data?.graph;
  const persistedQueryList = graph?.persistedQueryList;
  const operation = persistedQueryList?.operation;

  return (
    <div
      className={classnames(
        'h-full overflow-y-auto border-l border-l-primary bg-primary',
        isCollapsed
          ? 'w-[3.75rem]'
          : 'w-[35.25rem] max-lg:absolute max-lg:right-0 max-lg:top-0 max-lg:z-50 max-lg:h-full max-lg:w-120',
      )}
    >
      <div
        className={classnames(
          'sticky top-0 z-10 space-y-3 bg-primary py-3',
          isCollapsed
            ? 'flex flex-col items-center'
            : 'border-b border-b-primary px-6',
          loading && 'opacity-50',
        )}
      >
        <div className="flex items-center space-x-3">
          <Tooltip label={isCollapsed ? 'Expand' : 'Collapse'} placement="left">
            <IconButton
              variant="secondary"
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
              onClick={() => setIsCollapsed(!isCollapsed)}
              size="md"
              icon={
                isCollapsed ? (
                  <IconDoubleChevronLeft />
                ) : (
                  <IconDoubleChevronRight />
                )
              }
            />
          </Tooltip>
          <Heading
            variant="heading"
            size="2xl"
            className={classnames('truncate', isCollapsed && 'hidden')}
          >
            {operation?.name ?? 'unknown'}
          </Heading>
        </div>
        <div className="flex items-center space-x-2">
          <Text
            variant="subtitle"
            className={classnames(
              'inline-flex w-0 flex-1 flex-nowrap',
              isCollapsed && 'hidden',
            )}
            title={operation?.id}
          >
            {operation?.id ? (
              <>
                <span className="flex-[0_1_content] truncate">
                  {operation.id.substring(0, operation.id.length - 8)}
                </span>
                <span className="inline-block flex-[1_0_content] whitespace-nowrap">
                  {operation.id.substring(operation.id.length - 8)}
                </span>
              </>
            ) : (
              'unknown'
            )}
          </Text>
        </div>
      </div>
      {operation ? (
        <div
          className={classnames(
            'flex min-h-0 flex-1 flex-col space-y-4 p-6',
            loading && 'opacity-50',
            isCollapsed && 'hidden',
          )}
        >
          <div className="flex flex-col space-y-2 rounded-lg border border-primary bg-secondary p-4">
            <div className="flex items-center space-x-2">
              <IconTime className="w-4" />
              <Text variant="body">
                Created{' '}
                <Text as="span" variant="bodySb">
                  <RelativeTimestamp value={operation.firstPublishedAt} />
                </Text>
              </Text>
            </div>
            <div className="flex items-center space-x-2">
              <IconHistory className="w-4" />
              <Text variant="body">
                Last published{' '}
                <Text as="span" variant="bodySb">
                  <RelativeTimestamp value={operation.lastPublishedAt} />
                </Text>
              </Text>
            </div>
            <div className="flex items-center space-x-2">
              <IconClients className="w-4" />
              <Text variant="body" className="italic">
                {/* TODO(evan-pq): Is this the most helpful text for users? */}
                {operation.clientName ?? 'No client restrictions'}
              </Text>
            </div>
          </div>
          <div className="flex flex-col rounded-lg border border-primary bg-primary">
            <div className="flex items-center justify-between border-b border-b-primary px-4 py-2">
              <div className="flex items-center space-x-2">
                <Text variant="bodySb">Operation signature</Text>
                {/* TODO: We should display the Operation Signature ID, not the PQ ID here */}
                {/* <Code
                  title={operation.id}
                  className="max-w-28 truncate"
                  canCopy
                >
                  {operation.id}
                </Code> */}
              </div>
              <ButtonGroup>
                {linkedGraphVariant ? (
                  <IconButton
                    variant="secondary"
                    aria-label="Open in Explorer"
                    as={ExplorerLink}
                    graphId={graphId}
                    graphVariant={linkedGraphVariant}
                    document={operation.body}
                    label="Open in Explorer"
                    icon={<IconExplorer />}
                    shouldOpenInNewTab
                  />
                ) : (
                  <Tooltip
                    label="Copy operation"
                    clickedLabel="Copied"
                    placement="top"
                  >
                    <IconButton
                      aria-label="Copy operation"
                      variant="secondary"
                      icon={<IconCopy />}
                      onClick={() => {
                        copy(operation.body);
                      }}
                    />
                  </Tooltip>
                )}
              </ButtonGroup>
            </div>
            <div className="max-h-64 overflow-y-auto">
              <GraphqlCodeBlock
                sdl={operation.body}
                className="p-4 text-base"
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={classnames(
            'flex flex-1 items-center justify-center',
            isCollapsed && 'hidden',
          )}
        >
          <Loading />
        </div>
      )}
    </div>
  );
};
